import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import type { CompiledSchema, FormField, MapsTo } from '@re/form-engine';
import { collectConditions, DISPLAY_TYPES, evaluate } from '@re/form-engine';
import { DynamicForm } from '@re/form-ui';
import { Alert, Box, ButtonBase, Dialog, DialogContent, LinearProgress, Stack, Switch, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError, uploadFile } from '../api/client';
import { loadLocations, useLookups } from '../api/lookups';
import { useMapSupport } from '../api/map';
import type { SubmitResult } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { Button } from '../components/button';
import { ErrorState } from '../components/common';
import { FormStepNav, MobileProgress } from '../components/form-steps';
import { FormPageSkeleton } from '../components/skeletons';
import { useToast } from '../components/Toast';
import { buildSteps, stepTitle } from '../lib/form-steps';
import { getUiLanguage, tr } from '../lib/i18n';

interface PublishedForm {
  formId: string;
  versionId: string;
  version: number;
  checksum: string | null;
  schema: CompiledSchema;
}

const CONTACT_MAPS = new Set(['contact_name', 'contact_phone', 'contact_email']);
const OFFER_MAPS = new Set<MapsTo>(['price', 'area', 'building_area', 'land_area', 'outer_area', 'images']);
const DEMAND_MAPS = new Set<MapsTo>(['price_range', 'area_range', 'inner_area_range', 'outer_area_range', 'land_area_range', 'building_area_range']);

/** OFFER → properties, DEMAND → requests, SERVICE → requests («خدماتنا») */
type Direction = 'OFFER' | 'DEMAND' | 'SERVICE';
type RequestTypeOption = { code: string; direction: Direction };

/** Codes a form actually names: the request-type field's own options, plus conditions pointing at it. */
function referencedRequestCodes(fields: FormField[]): Set<string> | null {
  const field = fields.find((f) => f.type === 'request_type');
  if (!field) return null;
  const fromOptions = (field.config.options ?? []).map((o) => o.value).filter(Boolean);
  const fromRules = fields.flatMap((f) => [...collectConditions(f.visibleWhen), ...collectConditions(f.requiredWhen)])
    .filter((c) => c.field === field.key && c.op !== 'is_empty' && c.op !== 'is_not_empty')
    .flatMap((c) => (Array.isArray(c.value) ? c.value : [c.value]))
    .filter((v): v is string => typeof v === 'string' && v.length > 0);
  const codes = new Set([...fromOptions, ...fromRules]);
  return codes.size ? codes : null;
}

/**
 * Directions this customer's form can actually produce.
 * A condition that names one type does not hide the others; it only counts as evidence
 * of that direction. A single direction — and no fields of the other — skips the question.
 */
function supportedDirections<T extends RequestTypeOption>(fields: FormField[], types: T[]): Direction[] {
  // the builder listed the request types this form offers → exactly their directions
  const listed = new Set((fields.find((f) => f.type === 'request_type')?.config.options ?? []).map((o) => o.value).filter(Boolean));
  if (listed.size) return (['OFFER', 'DEMAND', 'SERVICE'] as Direction[]).filter((d) => types.some((t) => t.direction === d && listed.has(t.code)));
  const maps = new Set(fields.map((f) => f.mapsTo).filter((m): m is MapsTo => !!m));
  const offerFields = [...OFFER_MAPS].some((m) => maps.has(m));
  const demandFields = [...DEMAND_MAPS].some((m) => maps.has(m));
  const referenced = referencedRequestCodes(fields);
  const referencedDirections = referenced
    ? [...new Set(types.filter((t) => referenced.has(t.code)).map((t) => t.direction))]
    : [];
  const present = new Set<Direction>(referencedDirections);
  if (offerFields) present.add('OFFER');
  if (demandFields) present.add('DEMAND');
  if (!present.size) types.forEach((t) => present.add(t.direction));
  // services are offered whenever one is active and the form allows it
  const allow = new Set((fields.find((f) => f.type === 'request_type')?.config.options ?? []).map((o) => o.value).filter(Boolean));
  if (types.some((t) => t.direction === 'SERVICE' && (!allow.size || allow.has(t.code)))) present.add('SERVICE');
  return (['OFFER', 'DEMAND', 'SERVICE'] as Direction[]).filter((d) => present.has(d));
}

/**
 * The form's contact block: contact fields + everything in the contact name's section (WhatsApp,
 * preferred method, the section title) — never the request type. Same rule as the backend
 * (submission.service contactBlockKeys).
 */
function contactBlockKeys(fields: FormField[]): Set<string> {
  const section = fields.find((f) => f.mapsTo === 'contact_name')?.section?.trim() || null;
  return new Set(
    fields
      .filter((f) => f.type !== 'request_type' && ((f.mapsTo && CONTACT_MAPS.has(f.mapsTo)) || (section !== null && f.section?.trim() === section)))
      .map((f) => f.key),
  );
}

/** Inside the company the contact block is about someone else: the owner (offer) or the client (request). */
function internalLabels(fields: FormField[], direction: Direction | undefined, block: Set<string>): FormField[] {
  if (!direction) return fields;
  const titles: Record<string, { ar: string; en: string }> =
    direction === 'OFFER'
      ? {
          section: { ar: 'معلومات المالك / البائع', en: 'Owner / seller details' },
          contact_name: { ar: 'اسم المالك', en: "Owner's name" },
          contact_phone: { ar: 'رقم هاتف المالك', en: "Owner's phone" },
          contact_email: { ar: 'بريد المالك', en: "Owner's email" },
        }
      : {
          section: { ar: 'معلومات العميل', en: 'Client details' },
          contact_name: { ar: 'اسم العميل', en: "Client's name" },
          contact_phone: { ar: 'رقم هاتف العميل', en: "Client's phone" },
          contact_email: { ar: 'بريد العميل', en: "Client's email" },
        };
  return fields.map((f) => {
    if (!block.has(f.key)) return f;
    const t = f.type === 'section' ? titles.section : f.mapsTo ? titles[f.mapsTo] : undefined;
    return t ? { ...f, label: t } : f;
  });
}

const newKey = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
const Panel = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ bgcolor: '#fff', border: 1, borderColor: 'divider', borderRadius: '24px', overflow: 'hidden' }}>{children}</Box>
);

/** Staff entry — the published form in the public form's layout, adjusted for the company. */
export function NewEntryPage() {
  const { me, can } = useAuth();
  const mapSupport = useMapSupport();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedKind = (['OFFER', 'DEMAND', 'SERVICE'] as const).find((k) => k === searchParams.get('kind')) ?? null;
  const qc = useQueryClient();
  const { toastError } = useToast();
  const m3 = useTheme().palette.m3;
  const en = getUiLanguage() === 'en';
  const draftKey = `org.draft.${me?.user.id}`;
  const form = useQuery({ queryKey: ['form'], queryFn: () => api<PublishedForm>('/form'), staleTime: 60_000 });
  const lookups = useLookups();

  const [value, setValue] = useState<Record<string, unknown>>(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem(draftKey) ?? '{}') as Record<string, unknown>;
      return Object.fromEntries(Object.entries(parsed).filter(([k]) => !k.startsWith('_preview_')));
    } catch {
      return {};
    }
  });
  const [companyOwned, setCompanyOwned] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [showValidation, setShowValidation] = useState(false);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<SubmitResult | null>(null);
  const [copied, setCopied] = useState(false);
  const idem = useRef(newKey()); // one key per logical submission → double clicks / retries do not duplicate
  const top = useRef<HTMLDivElement>(null);

  // local draft (per employee) so a refresh does not lose a half-filled entry
  useEffect(() => {
    try {
      if (Object.keys(value).length) localStorage.setItem(draftKey, JSON.stringify(value));
      else localStorage.removeItem(draftKey);
    } catch {
      /* ignore */
    }
  }, [value, draftKey]);

  const availableDirections = useMemo(
    () =>
      (form.data && lookups.data ? supportedDirections(form.data.schema.fields, lookups.data.requestTypes) : [])
        // a property needs «properties.create», a request or a service «requests.create»
        .filter((d) => can(d === 'OFFER' ? 'properties.create' : 'requests.create')),
    [form.data, lookups.data, can],
  );
  const needsChoice = availableDirections.length > 1;
  const intent = needsChoice ? (requestedKind && availableDirections.includes(requestedKind) ? requestedKind : null) : availableDirections[0] ?? null;
  const allowedCodes = useMemo(() => {
    const options = form.data?.schema.fields.find((f) => f.type === 'request_type')?.config.options ?? [];
    return new Set(options.map((o) => o.value).filter(Boolean));
  }, [form.data]);

  // a saved draft must not keep a type this form does not offer
  useEffect(() => {
    if (!intent || !form.data || !lookups.data) return;
    const field = form.data.schema.fields.find((f) => f.type === 'request_type');
    if (!field) return;
    const current = value[field.key];
    const match = lookups.data.requestTypes.find((r) => r.code === current || r.id === current);
    const allowed = new Set(
      lookups.data.requestTypes
        .filter((t) => t.direction === intent && (!allowedCodes.size || allowedCodes.has(t.code)))
        .map((t) => t.code),
    );
    if (!match || allowed.has(match.code)) return;
    setValue((v) => {
      const next = { ...v };
      delete next[field.key];
      return next;
    });
  }, [intent, form.data, lookups.data, value, allowedCodes]);

  const formLookups = useMemo(() => {
    if (!lookups.data) return lookups.data;
    const types = lookups.data.requestTypes.filter((t) => (!intent || t.direction === intent) && (!allowedCodes.size || allowedCodes.has(t.code)));
    return { ...lookups.data, requestTypes: types.length ? types : lookups.data.requestTypes };
  }, [lookups.data, intent, allowedCodes]);

  const direction = useMemo(() => {
    const rtField = form.data?.schema.fields.find((f) => f.type === 'request_type');
    const v = rtField ? value[rtField.key] : undefined;
    return lookups.data?.requestTypes.find((r) => r.code === v || r.id === v)?.direction;
  }, [form.data, lookups.data, value]);
  const block = useMemo(() => contactBlockKeys(form.data?.schema.fields ?? []), [form.data]);
  const ownedByCompany = companyOwned && direction === 'OFFER';

  // the same published form, adjusted for staff: owner/client wording, and no contact block for company property
  const schema = useMemo<CompiledSchema | null>(() => {
    if (!form.data) return null;
    const fields = internalLabels(form.data.schema.fields, direction, block);
    return { ...form.data.schema, fields: ownedByCompany ? fields.filter((f) => !block.has(f.key)) : fields };
  }, [form.data, direction, block, ownedByCompany]);

  const ev = useMemo(() => (schema ? evaluate(schema, value) : null), [schema, value]);
  const typeOf = useMemo(() => new Map((schema?.fields ?? []).map((f) => [f.key, f.type])), [schema]);
  const allSteps = useMemo(() => (schema ? buildSteps(schema) : []), [schema]);
  // only steps that currently have a visible input; a step is titled by its first VISIBLE section header
  // (a step can hold one header per kind of request, e.g. «السعر والدفع» / «ملاحظات»)
  const steps = useMemo(
    () =>
      allSteps
        .filter((s) => s.keys.some((k) => ev?.visible[k] && !DISPLAY_TYPES.has(typeOf.get(k)!)))
        .map((s) => {
          const header = (schema?.fields ?? []).find((f) => f.type === 'section' && s.keys.includes(f.key) && ev?.visible[f.key]);
          return header ? { ...s, title: stepTitle(header.label) || s.title } : s;
        }),
    [allSteps, ev, typeOf, schema],
  );
  const idx = Math.min(stepIdx, Math.max(steps.length - 1, 0));
  const step = steps[idx];
  const multi = steps.length > 1;
  const last = idx >= steps.length - 1;
  const errorsIn = (keys: string[]) => (ev?.errors ?? []).filter((e) => keys.some((k) => e.field === k || e.field.startsWith(`${k}.`)) || keys.some((k) => serverErrors[k]));
  const errorSteps = useMemo(
    () => (showValidation ? new Set(steps.map((s, i) => (errorsIn(s.keys).length ? i : -1)).filter((i) => i >= 0)) : new Set<number>()),
    [showValidation, steps, ev, serverErrors], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const scrollTop = () => top.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const go = (i: number) => {
    setStepIdx(i);
    scrollTop();
  };

  const reset = () => {
    setValue({});
    setCompanyOwned(false);
    setStepIdx(0);
    setShowValidation(false);
    setServerErrors({});
    setNotice(null);
    idem.current = newKey();
  };

  const next = () => {
    if (!step) return;
    if (errorsIn(step.keys).length) {
      setShowValidation(true);
      setNotice(tr('أكمل الحقول المطلوبة في هذه الخطوة', 'Complete the required fields in this step'));
      return;
    }
    setNotice(null);
    go(idx + 1);
  };

  const goToStep = (i: number) => {
    if (i === idx) return;
    if (i < idx) return go(i);
    for (let j = idx; j < i; j++) {
      if (errorsIn(steps[j]!.keys).length) {
        setShowValidation(true);
        setNotice(tr('أكمل الحقول المطلوبة في هذه الخطوة', 'Complete the required fields in this step'));
        return go(j);
      }
    }
    go(i);
  };

  const submit = async () => {
    if (!form.data || !ev) return;
    setShowValidation(true);
    setServerErrors({});
    if (ev.errors.length) {
      const firstBad = steps.findIndex((s) => errorsIn(s.keys).length);
      if (firstBad >= 0) go(firstBad);
      setNotice(tr('راجع الحقول المظللة قبل الحفظ', 'Review the highlighted fields before saving'));
      return;
    }
    setBusy(true);
    try {
      const r = await api<SubmitResult>('/submissions', {
        method: 'POST',
        body: {
          formVersionId: form.data.versionId,
          answers: ownedByCompany ? Object.fromEntries(Object.entries(value).filter(([k]) => !block.has(k))) : value,
          companyOwned: ownedByCompany,
        },
        headers: { 'Idempotency-Key': idem.current },
      });
      setDone(r);
      reset();
      void qc.invalidateQueries({ queryKey: [r.entity === 'PROPERTY' ? 'properties' : 'requests'] });
      void qc.invalidateQueries({ queryKey: ['stats'] });
    } catch (e) {
      if (e instanceof ApiError && e.status === 410) {
        setNotice(tr('تم تحديث النموذج — راجع الحقول ثم احفظ مجدداً', 'The form was updated — review the fields and save again'));
        await form.refetch();
      } else if (e instanceof ApiError && Object.keys(e.fieldErrors()).length) {
        const fe = e.fieldErrors();
        setServerErrors(fe);
        const bad = steps.findIndex((s) => s.keys.some((k) => Object.keys(fe).some((f) => f === k || f.startsWith(`${k}.`))));
        if (bad >= 0) go(bad);
        setNotice(e.message);
      } else toastError(e);
    } finally {
      setBusy(false);
    }
  };

  const copyRef = async () => {
    if (!done) return;
    try {
      await navigator.clipboard.writeText(done.refNo);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  if (form.isLoading || lookups.isLoading) return <FormPageSkeleton />;
  if (form.isError) return <ErrorState error={form.error} onRetry={() => form.refetch()} />;
  if (lookups.isError) return <ErrorState error={lookups.error} onRetry={() => lookups.refetch()} />;
  if (!form.data || !lookups.data || !formLookups || !schema || !ev) return null;

  if (needsChoice && !intent) {
    const typeName = (r: { name_ar: string; name_en?: string | null }) => (en && r.name_en ? r.name_en : r.name_ar);
    const choices = [
      {
        kind: 'OFFER' as const,
        title: tr('عقار', 'Property'),
        text: tr('عقار معروض للبيع أو الإيجار. يُحفظ في العقارات ويدخل في المطابقة مباشرة.', 'A property for sale or rent. Saved to Properties and matched right away.'),
        icon: <HomeWorkOutlinedIcon />,
      },
      {
        kind: 'DEMAND' as const,
        title: tr('طلب عميل', 'Client request'),
        text: tr('عميل يبحث عن عقار للشراء أو الاستئجار. يُحفظ في الطلبات.', 'A client looking to buy or rent. Saved to Requests.'),
        icon: <AssignmentOutlinedIcon />,
      },
      {
        kind: 'SERVICE' as const,
        title: tr('طلب خدمة', 'Service request'),
        text: tr('معاملة لدى دائرة الأراضي والمساحة: بيع، رهن، إفراز، تأجير ورخص. يُحفظ في الطلبات.', 'A Lands Department transaction: sale, mortgage, partition, lease or licence. Saved to Requests.'),
        icon: <HandshakeOutlinedIcon />,
      },
    ].filter((c) => availableDirections.includes(c.kind));
    const Chevron = en ? ArrowForwardIcon : ArrowBackIcon;
    const SHOWN = 4;
    return (
      <Box sx={{ width: '100%', maxWidth: 720, pt: { xs: 0, md: 1 } }}>
        <Typography component="h2" variant="h5" fontWeight={600} sx={{ mb: 0.5 }}>{tr('ماذا تريد أن تضيف؟', 'What do you want to add?')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {tr('اختر نوع الإدخال، وسيفتح النموذج بالحقول الخاصة به فقط.', 'Pick what you are adding and the form opens with just its fields.')}
        </Typography>
        <Stack spacing={1.5}>
          {choices.map((c) => {
            const names = (lookups.data?.requestTypes ?? []).filter((r) => r.direction === c.kind && (!allowedCodes.size || allowedCodes.has(r.code))).map(typeName);
            const more = names.length - SHOWN;
            return (
              <ButtonBase
                key={c.kind}
                onClick={() => setSearchParams({ kind: c.kind })}
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  borderRadius: '24px',
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: { xs: 1.5, sm: 2 },
                  textAlign: 'start',
                  width: '100%',
                  transition: 'border-color .15s, background-color .15s',
                  '&:hover': { borderColor: m3.primary, bgcolor: m3.surfaceContainerLow },
                  '&:hover .chev': { transform: en ? 'translateX(3px)' : 'translateX(-3px)', color: m3.primary },
                  '&.Mui-focusVisible': { outline: `2px solid ${m3.primary}`, outlineOffset: 2 },
                }}
              >
                <Box sx={{ width: 52, height: 52, borderRadius: '16px', display: 'grid', placeItems: 'center', bgcolor: m3.primaryContainer, color: m3.onPrimaryContainer, flexShrink: 0, alignSelf: 'flex-start' }}>
                  {c.icon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={600} sx={{ fontSize: 17 }}>{c.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{c.text}</Typography>
                  {names.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.25 }}>
                      {names.slice(0, SHOWN).map((n) => (
                        <Box key={n} component="span" sx={{ px: 1.25, py: 0.25, borderRadius: '999px', bgcolor: m3.surfaceContainerLow, border: 1, borderColor: 'divider', fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                          {n}
                        </Box>
                      ))}
                      {more > 0 && (
                        <Box component="span" sx={{ px: 1.25, py: 0.25, fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                          {tr(`و${more} أخرى`, `+${more} more`)}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
                <Chevron className="chev" sx={{ color: 'text.secondary', flexShrink: 0, transition: 'transform .15s, color .15s' }} />
              </ButtonBase>
            );
          })}
        </Stack>
      </Box>
    );
  }

  const Fwd = en ? ArrowForwardIcon : ArrowBackIcon;
  const Bwd = en ? ArrowBackIcon : ArrowForwardIcon;
  const hasValue = Object.keys(value).length > 0;

  // what is being entered + the company-owned switch (side column on desktop, above the form on phones)
  const contextCard = (
    <Box sx={{ bgcolor: '#fff', border: 1, borderColor: 'divider', borderRadius: '24px', p: 2.5 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ width: 44, height: 44, borderRadius: '14px', display: 'grid', placeItems: 'center', bgcolor: m3.primaryContainer, color: m3.onPrimaryContainer, flexShrink: 0 }}>
          {direction === 'SERVICE' ? <HandshakeOutlinedIcon /> : direction === 'DEMAND' ? <AssignmentOutlinedIcon /> : <HomeWorkOutlinedIcon />}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography fontWeight={600}>
            {direction === 'OFFER' ? tr('عقار جديد', 'New property') : direction === 'DEMAND' ? tr('طلب جديد', 'New request') : direction === 'SERVICE' ? tr('طلب خدمة', 'Service request') : tr('إدخال جديد', 'New entry')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {direction === 'OFFER'
              ? tr('سيُحفظ في العقارات — متاح مباشرة', 'Saved to Properties — available right away')
              : direction === 'DEMAND' || direction === 'SERVICE'
                ? tr('سيُحفظ في الطلبات', 'Saved to Requests')
                : tr('اختر نوع الطلب للبدء', 'Choose the request type to start')}
          </Typography>
        </Box>
      </Stack>
      {direction === 'OFFER' && block.size > 0 && (
        <Box
          component="label"
          sx={{
            mt: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            borderRadius: '16px',
            cursor: 'pointer',
            bgcolor: companyOwned ? m3.primaryContainer : m3.surfaceContainerLow,
            transition: 'background-color .2s',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600}>{tr('العقار ملك الشركة', 'Company-owned property')}</Typography>
            <Typography variant="caption" color="text.secondary">{tr('بدون مالك خارجي — بدون معلومات تواصل', 'No outside owner — no contact details')}</Typography>
          </Box>
          <Switch checked={companyOwned} onChange={(e) => setCompanyOwned(e.target.checked)} disabled={busy} />
        </Box>
      )}
      {needsChoice && (
        <ButtonBase onClick={() => setSearchParams({})} sx={{ mt: 2, color: m3.primary, fontSize: 13, fontWeight: 600 }}>
          {tr('تغيير نوع الإدخال', 'Change what you are adding')}
        </ButtonBase>
      )}
    </Box>
  );

  return (
    <Box sx={{ width: '100%', maxWidth: 1120, mx: 'auto' }}>
      <Box ref={top} sx={{ scrollMarginTop: 96 }} />
      <Box sx={{ display: 'grid', gap: { xs: 2, md: 3 }, gridTemplateColumns: { xs: '1fr', md: '300px minmax(0, 1fr)' }, alignItems: 'start' }}>
        <Box sx={{ position: { md: 'sticky' }, top: { md: 88 }, display: 'grid', gap: 2 }}>
          {contextCard}
          {multi && (
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <FormStepNav steps={steps} activeIndex={idx} maxReachableIndex={idx + 1} errorSteps={errorSteps} onSelect={goToStep} />
            </Box>
          )}
        </Box>

        <Panel>
          {busy && <LinearProgress sx={{ height: 3 }} />}
          <Box sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
            {multi && <MobileProgress steps={steps} activeIndex={idx} />}
            <Typography component="h2" variant="h5" sx={{ mb: 0.75 }}>
              {multi ? step?.title || tr(`الخطوة ${idx + 1}`, `Step ${idx + 1}`) : tr('إدخال جديد', 'New entry')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {tr('المسودة محفوظة تلقائياً على هذا الجهاز حتى تحفظ الإدخال.', 'Your draft is kept on this device until you save the entry.')}
            </Typography>

            {notice && (
              <Alert severity="warning" sx={{ mb: 3, borderRadius: '12px' }} onClose={() => setNotice(null)}>
                {notice}
              </Alert>
            )}

            <DynamicForm
              lang={en ? 'en' : 'ar'}
              schema={schema}
              lookups={formLookups}
              value={value}
              onChange={(v) => {
                setValue(v);
                if (Object.keys(serverErrors).length) setServerErrors({});
              }}
              errors={serverErrors}
              showValidation={showValidation}
              loadLocations={loadLocations}
              defaultCountry={me?.organization.default_country_id}
              uploadFile={uploadFile}
              map={mapSupport}
              disabled={busy}
              // the step title already shows the section heading → do not repeat it
              renderOnly={multi ? step?.keys.filter((k) => typeOf.get(k) !== 'section') : undefined}
            />
          </Box>

          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              zIndex: 2,
              bgcolor: 'rgba(255,255,255,.96)',
              backdropFilter: 'blur(8px)',
              borderTop: 1,
              borderColor: 'divider',
              px: { xs: 2, sm: 3, md: 4 },
              py: 1.5,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Button startIcon={<RestartAltIcon />} onClick={reset} disabled={busy || !hasValue} sx={{ display: { xs: idx > 0 ? 'none' : 'inline-flex', sm: 'inline-flex' } }}>
                {tr('تفريغ', 'Clear')}
              </Button>
              <Box sx={{ flex: 1 }} />
              {multi && idx > 0 && (
                <Button variant="outlined" size="large" startIcon={<Bwd />} onClick={() => go(idx - 1)} disabled={busy}>
                  {tr('السابق', 'Back')}
                </Button>
              )}
              {multi && !last ? (
                <Button variant="contained" size="large" endIcon={<Fwd />} onClick={next} disabled={busy} sx={{ minWidth: 140 }}>
                  {tr('التالي', 'Next')}
                </Button>
              ) : (
                <Button variant="contained" size="large" startIcon={<SaveOutlinedIcon />} onClick={submit} loading={busy} sx={{ minWidth: 160 }}>
                  {direction === 'OFFER' ? tr('حفظ العقار', 'Save property') : direction === 'DEMAND' || direction === 'SERVICE' ? tr('حفظ الطلب', 'Save request') : tr('حفظ الإدخال', 'Save entry')}
                </Button>
              )}
            </Stack>
          </Box>
        </Panel>
      </Box>

      <Dialog open={!!done} onClose={() => setDone(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '28px' } }}>
        <DialogContent>
          <Stack alignItems="center" spacing={1.5} sx={{ textAlign: 'center', pt: 2 }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'success.main',
                color: '#fff',
                '@keyframes pop': { from: { transform: 'scale(.6)', opacity: 0 }, to: { transform: 'scale(1)', opacity: 1 } },
                animation: 'pop 320ms cubic-bezier(.2,0,0,1.2) both',
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              }}
            >
              <CheckIcon sx={{ fontSize: 40 }} />
            </Box>
            <Typography variant="h6">{done?.entity === 'PROPERTY' ? tr('تمت إضافة العقار', 'Property added') : tr('تم تسجيل الطلب', 'Request created')}</Typography>
            <Box sx={{ width: '100%', bgcolor: m3.surfaceContainerLow, borderRadius: '16px', p: 2 }}>
              <Typography variant="body2" color="text.secondary">{tr('الرقم المرجعي', 'Reference number')}</Typography>
              <Typography dir="ltr" sx={{ fontSize: 26, fontWeight: 600, letterSpacing: 1, fontVariantNumeric: 'tabular-nums', my: 0.5 }}>{done?.refNo}</Typography>
              <Button size="small" startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />} onClick={copyRef}>
                {copied ? tr('تم النسخ', 'Copied') : tr('نسخ الرقم', 'Copy number')}
              </Button>
            </Box>
            <Stack direction="row" spacing={1} sx={{ width: '100%', pt: 1, pb: 1 }}>
              <Button fullWidth variant="outlined" onClick={() => setDone(null)}>{tr('إدخال آخر', 'Add another')}</Button>
              <Button fullWidth variant="contained" onClick={() => done && nav(done.entity === 'PROPERTY' ? `/properties/${done.id}` : `/requests/${done.id}`)}>
                {tr('فتح السجل', 'Open record')}
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
